import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import UiSelect from './components/UiSelect.jsx';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Dashboard } from './pages/Dashboard.jsx';
import { StudentPage } from './pages/Students.jsx';
import StudentEditorModal, { prefetchStudentFormData } from './pages/StudentEditorModal.jsx';
import { StudentFormPage } from './pages/StudentForm.jsx';
import { EmployeesPage } from './pages/Employees.jsx';
import { SettingsPage } from './pages/Settings.jsx';
import { ProfilePage } from './pages/Profile.jsx';
import { LoginPage } from './pages/Login.jsx';
import SuperAdminLogin from './pages/SuperAdminLogin.jsx';
import SuperAdminDashboard from './pages/SuperAdminDashboard.jsx';
import { SetupGuide } from './pages/SetupGuide.jsx';
import { SeatsPage } from './pages/Seats.jsx';
import { LibrariesPage } from './pages/Libraries.jsx';
import { RoomsPage } from './pages/Rooms.jsx';
import { PlansPage } from './pages/PaymentPlans.jsx';
import { PaymentsPage } from './pages/Payments.jsx';
import { ReportsPage } from './pages/Reports.jsx';
import { Sidebar } from './header/Sidebar.jsx';
import { AppHeader } from './header/AppHeader.jsx';
import API from './api/index.js';
import { clearApiDataCache, clearTenantSession } from './api/api1.js';
import { checkAuth } from './services/auth.js';
import { DEMO_LOGIN_ENABLED } from './demoMode.js';
import { clearServiceApiCache, updateStudent, deleteStudent } from './services/api.js';
import emailService from './services/emailService.js';
import { clearSeatsCache } from './utils/seatsCache.js';
import { clearSeatTypesCache } from './services/seatTypes.js';
import { useToast } from './context/ToastContext.jsx';
import { LibraryProvider } from './context/LibraryContext.jsx';
import SessionManager from './components/SessionManager.jsx';
import SeatSelectionModal from './pages/SeatSelectionModal.jsx';
import { getLastActivity, isSessionExpired, touchSession } from './utils/sessionTimeout.js';

const PAGE_PATHS = {
  Dashboard: '/dashboard',
  Students: '/students',
  'Student Form': '/students/add',
  Seats: '/seats',
  Libraries: '/libraries',
  Rooms: '/rooms',
  'Payment Plans': '/payment-plans',
  Payments: '/payments',
  Reports: '/reports',
  Employees: '/employees',
  Settings: '/settings',
  Profile: '/profile',
  'Setup Guide': '/setup-guide',
};

const PATH_PAGES = Object.entries(PAGE_PATHS).reduce((result, [page, path]) => {
  result[path] = page;
  return result;
}, { '/users': 'Employees', '/help': 'Setup Guide' });

export default function AppContent() {
  const [authenticated, setAuthenticated] = useState(DEMO_LOGIN_ENABLED);
  const [checking, setChecking] = useState(!DEMO_LOGIN_ENABLED);
  const [userRole, setUserRole] = useState(() => localStorage.getItem('userRole') || '');
  const [students, setStudents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [libraries, setLibraries] = useState([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('All');
  const [modal, setModal] = useState('');
  const [collapsed, setCollapsed] = useState(() => window.innerWidth <= 760);
  const [showPayments, setShowPayments] = useState(() =>
    window.location.pathname === '/payments' || window.location.pathname === '/payment-plans'
  );
  const [selectedLibrary, setSelectedLibrary] = useState('All Libraries');
  const [libraryMenuOpen, setLibraryMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('studyroom-theme') === 'dark');
  const [receiptPickerOpen, setReceiptPickerOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [paymentStudentId, setPaymentStudentId] = useState(null);
  const [studentRefreshKey, setStudentRefreshKey] = useState(0);
  const authDataGeneration = useRef(0);
  const [form, setForm] = useState({ name:'', phone:'', email:'', plan:'Monthly', library:'Beed', seat:'', status:'Active' });
  const navigate = useNavigate();
  const location = useLocation();
  const page = PATH_PAGES[location.pathname] || 'Dashboard';
  useEffect(() => {
    setShowPayments(page === 'Payments' || page === 'Payment Plans');
  }, [page]);
  useEffect(() => {
    const syncSidebarToScreen = () => setCollapsed(window.innerWidth <= 760);
    window.addEventListener('resize', syncSidebarToScreen);
    return () => window.removeEventListener('resize', syncSidebarToScreen);
  }, []);
  const setPage = useCallback(target => {
    const path = PAGE_PATHS[target];
    if (path) navigate(path);
    if (window.innerWidth <= 760) setCollapsed(true);
  }, [navigate]);
  const { showToast } = useToast();

  useEffect(() => {
    document.documentElement.dataset.theme = darkMode ? 'dark' : 'light';
    localStorage.setItem('studyroom-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    if (!logoutConfirmOpen) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') setLogoutConfirmOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [logoutConfirmOpen]);

  useEffect(() => {
    if (DEMO_LOGIN_ENABLED) {
      setChecking(false);
      return undefined;
    }

    let cancelled = false;

    // If the idle timeout elapsed while the app/tab was closed, the stored
    // session is no longer trustworthy: drop it and show the login screen
    // instead of letting the server cookie silently re-authenticate.
    if (isSessionExpired()) {
      clearTenantSession();
      clearApiDataCache();
      clearServiceApiCache();
      clearSeatsCache();
      clearSeatTypesCache();
      setAuthenticated(false);
      setUserRole('');
      setChecking(false);
      return undefined;
    }

    (async () => {
      const storedUser = localStorage.getItem('userinfo');
      const hasStoredUser = Boolean(storedUser && !['{}', 'null', 'undefined'].includes(storedUser));
      const storedRole = localStorage.getItem('userRole') || '';
      try {
        const data = await checkAuth();
        if (cancelled) return;
        const isAuthenticated = Boolean(data?.authenticated ?? data?.isAuthenticated ?? data?.is_authenticated);
        if (isAuthenticated) {
          const role = data?.role || data?.user?.role || data?.user?.user_type || storedRole;
          setAuthenticated(true);
          setUserRole(role || '');
          if (role) localStorage.setItem('userRole', role);
          if (!hasStoredUser && data?.user) {
            localStorage.setItem('userinfo', JSON.stringify({
              email: typeof data.user === 'string' ? data.user : data.user.email,
              role,
            }));
          }
          // Never refresh the idle clock during startup: an existing timestamp
          // carries the real elapsed idle time and must be preserved.
          if (!getLastActivity()) touchSession();
        } else if (hasStoredUser) {
          setAuthenticated(true);
          setUserRole(storedRole);
        } else {
          setAuthenticated(false);
        }
      } catch (error) {
        if (cancelled) return;
        if (hasStoredUser) {
          setAuthenticated(true);
          setUserRole(storedRole);
        } else {
          setAuthenticated(false);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!authenticated || DEMO_LOGIN_ENABLED) return;
    const generation = authDataGeneration.current;
    let cancelled = false;
    prefetchStudentFormData();
    API.get('/api/students').then(res => {
      const d = res.data?.students || res.data?.data || res.data;
      if (!cancelled && generation === authDataGeneration.current && Array.isArray(d)) setStudents(d);
    }).catch(error => {
      if (!cancelled) console.error('Failed to fetch students', error);
    });
    API.get('/api/libraries').then(res => {
      const d = res.data?.libraries || res.data;
      if (!cancelled && generation === authDataGeneration.current && Array.isArray(d)) setLibraries(d);
    }).catch(error => {
      if (!cancelled) console.error('Failed to fetch libraries', error);
    });
    return () => { cancelled = true; };
  }, [authenticated, studentRefreshKey]);

  const visibleStudents = useMemo(() => {
    const allLibraries = !selectedLibrary || ['all', 'all libraries'].includes(String(selectedLibrary).toLowerCase());
    const selectedLibraryRecord = libraries.find(library => (
      String(library.id ?? library.library_id) === String(selectedLibrary) ||
      String(library.library_name || library.name || '').toLowerCase() === String(selectedLibrary).toLowerCase()
    ));
    const selectedLibraryName = selectedLibraryRecord?.library_name || selectedLibraryRecord?.name || '';

    return students.filter(student => {
      const searchableText = `${student.name || ''} ${student.phone || ''} ${student.library_name_display || student.library_name || student.library || ''}`.toLowerCase();
      const matchesSearch = searchableText.includes(query.toLowerCase());
      const matchesStatus = filter === 'All' || (filter === 'Outstanding' ? student.due > 0 : student.status === filter);
      const studentLibraryId = student.library_id ?? student.libraryId ?? student.libraryid;
      const studentLibraryName = student.library_name_display || student.library_name || student.library || '';
      const matchesLibrary = allLibraries ||
        String(studentLibraryId ?? '') === String(selectedLibrary) ||
        String(studentLibraryName).toLowerCase() === String(selectedLibraryName || selectedLibrary).toLowerCase();
      return matchesSearch && matchesStatus && matchesLibrary;
    });
  }, [students, query, filter, selectedLibrary, libraries]);
  
  const toast = useCallback(message => {
    showToast(message, 'success', 3000);
  }, [showToast]);

  const openStudent = () => { setEditingStudent(null); setForm({ name:'', phone:'', email:'', plan:'Monthly', library:'Beed', seat:'', status:'Active' }); setModal('student'); };
  const openStudentForEdit = student => {
    setEditingStudent(student);
    setForm({
      name: student.name || student.fullName || '',
      phone: student.phone || '',
      email: student.email || '',
      plan: student.subscription_display || student.subscription_type || student.plan || student.plan_name || student.membership_type || 'Monthly',
      library: student.library_name_display || student.library || student.library_name || 'Beed',
      seat: student.seat_number_display || student.seatnumber || student.seat || student.seat_number || '',
      admissionDate: student.admissionDate || student.joined_date || student.joined || '',
      subscriptionStart: student.subscriptionStart || student.startDate || '',
      subscriptionEnd: student.subscriptionEnd || student.endDate || '',
      address: student.address || ''
    });
    setModal('student'); setPage('Students');
  };
  const saveStudent = async e => {
    e.preventDefault();
    if (!form.name.trim() ||!form.phone.trim()) return;
    if (editingStudent) {
      const studentId = editingStudent.studentId || editingStudent.id || editingStudent._id;
      try {
        if (!DEMO_LOGIN_ENABLED) await updateStudent(studentId, form);
        setStudents(current => current.map(s => String(s.studentId || s.id || s._id) === String(studentId)? {...s,...form } : s));
        setEditingStudent(null); setModal(''); setPage('Students'); toast('Student updated successfully');
      } catch (error) { toast(error?.message || 'Could not update student'); }
      return;
    }
    if (DEMO_LOGIN_ENABLED) {
      setStudents(current => [{...form, id: Date.now(), days:'30 days', seat:form.seat || 'â€”', due:0 },...current]);
      setModal(''); setPage('Students'); toast('Student added to this local preview'); return;
    }
    try { await API.post('/api/students', form); const res = await API.get('/api/students'); setStudents(res.data?.students || res.data); } catch { setStudents([{...form, id: Date.now(), days:'30 days', seat:form.seat || 'â€”', due:0 },...students]); }
    setModal(''); setPage('Students'); toast('Student added successfully');
  };
  const removeStudent = async student => {
    const studentId = student.studentId || student.id || student._id;
    try {
      if (!DEMO_LOGIN_ENABLED) await deleteStudent(studentId);
      setStudents(current => current.filter(item => String(item.studentId || item.id || item._id)!== String(studentId)));
      window.dispatchEvent(new CustomEvent('studentDataUpdated', {
        detail: { studentId, action: 'deleted' }
      }));
      toast('Student deleted successfully');
    } catch (error) { toast(error?.message || 'Could not delete student'); }
  };
  const notifyStudent = async student => {
    const studentId = student.student_id || student.studentId || student.studentid || student.id;
    const studentEmail = student.email;
    if (!studentEmail) { toast('This student has no email address'); return; }
    const due = Number(student.amount_due ?? student.total_amount_due ?? student.due ?? 0);
    try {
      await emailService.sendStudentEmail(studentId, due > 0 ? 'payment_reminder' : 'expiry_reminder', '', {
        studentEmail,
        studentName: student.name,
        libraryName: student.library_name_display || student.library_name || student.library,
        amount: due,
        dueDate: student.subscription_end,
        expiryDate: student.subscription_end,
      });
      toast(`Reminder sent to ${student.name}`);
    } catch (error) {
      toast(error?.message || 'Could not send reminder');
    }
  };
  const saveEmployee = e => { e.preventDefault(); const next={...form,id:`EM-${String(Date.now()).slice(-4)}`,joined:form.joiningDate}; setEmployees([next,...employees]); setModal(''); toast('Employee added successfully'); };
  const openPaymentForStudent = studentOrId => {
    const studentId = typeof studentOrId === 'object'
      ? (studentOrId.student_id || studentOrId.studentId || studentOrId.studentid || studentOrId.id)
      : studentOrId;
    setPaymentStudentId(studentId);
    setModal('');
    setPage('Payments');
  };
  const handleStudentSaved = (savedStudent) => {
    setEditingStudent(null);
    setModal('');
    setPage('Students');
    setStudentRefreshKey(key => key + 1);
    if (DEMO_LOGIN_ENABLED && savedStudent) {
      setStudents(current => {
        const savedId = savedStudent.student_id || savedStudent.studentId || savedStudent.studentid || savedStudent.id;
        const exists = current.some(student => String(student.studentId || student.student_id || student.id || student._id) === String(savedId));
        return exists
          ? current.map(student => String(student.studentId || student.student_id || student.id || student._id) === String(savedId) ? { ...student, ...savedStudent } : student)
          : [{ ...savedStudent, id: savedStudent.id || Date.now() }, ...current];
      });
    }
  };

  const handleLogin = useCallback(payload => {
    authDataGeneration.current += 1;
    clearApiDataCache();
    clearServiceApiCache();
    clearSeatsCache();
    clearSeatTypesCache();
    setStudents([]);
    setEmployees([]);
    setLibraries([]);
    setSelectedLibrary('All Libraries');
    setQuery('');
    setFilter('All');
    const role = payload?.user?.role || payload?.user?.user_type || payload?.role || localStorage.getItem('userRole') || '';
    touchSession();
    if (role) localStorage.setItem('userRole', role);
    setUserRole(role);
    setAuthenticated(true);
    navigate('/dashboard', { replace: true });
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    try {
      // Call backend logout endpoint to clear HttpOnly cookie
      await API.post('/api/logout');
    } catch (error) {
      console.warn('Logout API call failed:', error);
    }
    authDataGeneration.current += 1;
    clearApiDataCache();
    clearServiceApiCache();
    clearSeatsCache();
    clearSeatTypesCache();
    clearTenantSession();
    setAuthenticated(false);
    setUserRole('');
    setStudents([]);
    setEmployees([]);
    setLibraries([]);
    setSelectedLibrary('All Libraries');
    setQuery('');
    setFilter('All');
    setModal('');
    setEditingStudent(null);
    showToast('Logged out successfully', 'success', 3000);
    navigate('/', { replace: true });
  }, [navigate, showToast]);

  const requestLogout = useCallback(() => {
    setProfileMenuOpen(false);
    setLogoutConfirmOpen(true);
  }, []);

  const confirmLogout = useCallback(() => {
    setLogoutConfirmOpen(false);
    handleLogout();
  }, [handleLogout]);

  if (checking) {
    return (
      <div className="checking-screen">
        <span className="session-spinner" aria-hidden="true" />
        <p>Checking session...</p>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <Routes>
        <Route path="/" element={<LoginPage onLogin={handleLogin} />} />
        <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
        <Route path="/register" element={<Navigate to="/" replace />} />
        <Route path="/tenant-register" element={<Navigate to="/" replace />} />
        <Route path="/admin-register" element={<Navigate to="/" replace />} />
        <Route path="/superadmin/login" element={<SuperAdminLogin />} />
        <Route path="/superadmin" element={
          localStorage.getItem('super_admin_logged') === '1' && localStorage.getItem('superadmin_user')
            ? <SuperAdminDashboard onLogout={async () => {
              try {
                await API.post('/api/superadmin/logout');
              } catch (error) {
                console.warn('Superadmin logout API call failed:', error);
              }
              localStorage.removeItem('super_admin_logged');
              localStorage.removeItem('superadmin_user');
              navigate('/superadmin/login', { replace: true });
            }} />
            : <Navigate to="/superadmin/login" replace />
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <SessionManager userRole={userRole} onLogout={handleLogout} enabled={!DEMO_LOGIN_ENABLED}>
      <LibraryProvider>
        <div className="app-shell">
          {!collapsed && <button className="sidebar-backdrop" type="button" aria-label="Close navigation" onClick={() => setCollapsed(true)} />}
          <Sidebar page={page} setPage={setPage} collapsed={collapsed} setCollapsed={setCollapsed} showPayments={showPayments} setShowPayments={setShowPayments} onSignOut={requestLogout} />
          <main className="main-area">
            <AppHeader collapsed={collapsed} setCollapsed={setCollapsed} query={query} setQuery={setQuery} selectedLibrary={selectedLibrary} setSelectedLibrary={setSelectedLibrary} libraryMenuOpen={libraryMenuOpen} setLibraryMenuOpen={setLibraryMenuOpen} profileMenuOpen={profileMenuOpen} setProfileMenuOpen={setProfileMenuOpen} setPage={setPage} onSignOut={requestLogout} toast={toast} darkMode={darkMode} setDarkMode={setDarkMode} hideGlobalSearch={location.pathname === '/students'} />
            <div className="page-content">
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard setPage={setPage} openStudent={openStudent} toast={toast} selectedLibrary={selectedLibrary} libraries={libraries} />} />
                <Route path="/students" element={<StudentPage students={visibleStudents} query={query} setQuery={setQuery} filter={filter} setFilter={setFilter} openStudent={openStudent} onView={student => { setSelectedStudent(student); setModal('view-student'); }} onEdit={openStudentForEdit} onDelete={removeStudent} onNotify={notifyStudent} onPay={openPaymentForStudent} toast={showToast} />} />
                <Route path="/students/add" element={<StudentFormPage toast={toast} onSaved={handleStudentSaved} onCancel={() => setPage('Students')} onPayStudent={openPaymentForStudent} />} />
                <Route path="/seats" element={<SeatsPage selectedLibrary={selectedLibrary} />} />
                <Route path="/libraries" element={<LibrariesPage libraries={libraries} setLibraries={setLibraries} setPage={setPage} setSelectedLibrary={setSelectedLibrary} toast={toast} />} />
                <Route path="/rooms" element={<RoomsPage selectedLibrary={selectedLibrary} />} />
                <Route path="/payment-plans" element={<PlansPage />} />
                <Route path="/payments" element={<PaymentsPage toast={toast} receiptPickerOpen={receiptPickerOpen} onCloseReceiptPicker={() => setReceiptPickerOpen(false)} initialStudentId={paymentStudentId} />} />
                <Route path="/reports" element={<ReportsPage toast={toast} />} />
                <Route path="/employees" element={<EmployeesPage employees={employees} />} />
                <Route path="/users" element={<EmployeesPage employees={employees} />} />
                <Route path="/settings" element={<SettingsPage toast={toast} />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/setup-guide" element={<SetupGuide setPage={setPage} />} />
                <Route path="/help" element={<SetupGuide setPage={setPage} />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </div>
          </main>
          {modal === 'student' && <StudentEditorModal student={editingStudent} toast={toast} onClose={()=>setModal('')} onSaved={handleStudentSaved} onPayStudent={openPaymentForStudent} />}
          {modal && modal !== 'student' && <Modal kind={modal} form={form} setForm={setForm} close={()=>setModal('')} saveStudent={saveStudent} saveEmployee={saveEmployee} toast={toast} editingStudent={editingStudent} selectedStudent={selectedStudent} />}
          {logoutConfirmOpen && (
            <div
              className="logout-confirm-overlay"
              onMouseDown={event => { if (event.target === event.currentTarget) setLogoutConfirmOpen(false); }}
            >
              <div
                className="logout-confirm"
                role="dialog"
                aria-modal="true"
                aria-labelledby="logout-confirm-title"
                aria-describedby="logout-confirm-description"
              >
                <span className="logout-confirm-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
                    <path d="M10 8l-4 4 4 4M6 12h9" />
                  </svg>
                </span>
                <h2 id="logout-confirm-title">Sign out of Study Room?</h2>
                <p id="logout-confirm-description">You will be returned to the login screen. Any unsaved changes will be lost.</p>
                <div className="logout-confirm-actions">
                  <button type="button" className="button ghost" onClick={() => setLogoutConfirmOpen(false)} autoFocus>Stay signed in</button>
                  <button type="button" className="button red" onClick={confirmLogout}>Sign out</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </LibraryProvider>
    </SessionManager>
  );
}

function Modal({ kind, form, setForm, close, saveStudent, saveEmployee, toast, editingStudent, selectedStudent }) {
  const student = kind === 'student'; const employee = kind === 'employee'; const viewingStudent = kind === 'view-student';
  const [showSeatModal, setShowSeatModal] = useState(false);
  const title = student? editingStudent? 'Edit Student' : 'Add Student' : employee? 'Add Employee' : viewingStudent? 'Student Details' : `Add ${kind.split('-').map(x => x[0].toUpperCase() + x.slice(1)).join(' ')}`;
  return (
    <div className="modal-backdrop" onMouseDown={e => e.target === e.currentTarget && close()}>
      <div className={`modal ${employee? 'employee-modal' : ''}`}>
        <div className="modal-head"><div><span className="eyebrow">STUDY ROOM Â· WORKSPACE</span><h2>{title}</h2></div><button className="close-button" onClick={close}>Ã—</button></div>
        {viewingStudent? (
          <div className="student-details-modal">
            <div><span>Name</span><b>{selectedStudent?.name || selectedStudent?.fullName || 'â€”'}</b></div>
            <div><span>Student ID</span><b>{selectedStudent?.student_id_code || selectedStudent?.studentId || selectedStudent?.id || selectedStudent?._id || 'â€”'}</b></div>
            <div><span>Phone</span><b>{selectedStudent?.phone || 'â€”'}</b></div>
            <div><span>Email</span><b>{selectedStudent?.email || 'â€”'}</b></div>
            <div><span>Library</span><b>{selectedStudent?.library_name_display || selectedStudent?.library || selectedStudent?.library_name || 'â€”'}</b></div>
            <div><span>Seat</span><b>{selectedStudent?.seat_number_display || selectedStudent?.seat || selectedStudent?.seat_number || 'â€”'}{selectedStudent?.room_name_display && selectedStudent.room_name_display !== 'â€”' ? ` Â· ${selectedStudent.room_name_display}` : ''}</b></div>
            <div><span>Plan</span><b>{selectedStudent?.subscription_display || selectedStudent?.plan || selectedStudent?.plan_name || 'Monthly'}</b></div>
            <div><span>Days left</span><b>{selectedStudent?.days_remaining ?? 'â€”'}</b></div>
            <div><span>Amount due</span><b>â‚¹{Number(selectedStudent?.amount_due || 0).toLocaleString('en-IN')}</b></div>
            <div><span>Status</span><b>{selectedStudent?.status || 'Active'}</b></div>
            <div className="modal-actions"><button type="button" className="button ghost" onClick={close}>Close</button></div>
          </div>
        ) : student? (
          <>
            <form onSubmit={saveStudent}>
              <div className="form-grid">
                <label>Name <em>*</em><input required placeholder="Student full name" value={form.name} onChange={e => setForm({...form, name: e.target.value })} /></label>
                <label>Email<input type="email" placeholder="john@example.com" value={form.email} onChange={e => setForm({...form, email: e.target.value })} /></label>
                <label>Phone <em>*</em><div className="phone-input"><span>+91</span><input required placeholder="9876543210" value={form.phone} onChange={e => setForm({...form, phone: e.target.value })} /></div></label>
                <label>Admission date<input type="date" value={form.admissionDate || new Date().toISOString().split('T')[0]} onChange={e => setForm({...form, admissionDate: e.target.value })} /></label>
                <label>Subscription type<UiSelect value={form.plan} onChange={e => setForm({...form, plan: e.target.value })}><option>Monthly</option><option>Quarterly</option><option>Half Yearly</option><option>Yearly</option></UiSelect></label>
                <label>Library <em>*</em><UiSelect value={form.library} onChange={e => setForm({...form, library: e.target.value })}><option>Select Library</option><option>Beed</option><option>Pune</option><option>Hadapsar</option><option>Wagholi</option><option>Kharadi</option></UiSelect></label>
               <label>Selected seat <em>*</em>
  <div style={{display:'flex',gap:'6px'}}>
    <input required placeholder="e.g. 024" value={form.seat} readOnly style={{flex:1}}/>
    <button type="button" className="button primary" onClick={()=> setShowSeatModal(true)}>Select Seat</button>
  </div>

                </label>
                <label>Subscription start<em>*</em><input type="date" value={form.subscriptionStart || ''} onChange={e => setForm({...form, subscriptionStart: e.target.value })} /></label>
                <label>Subscription end<em>*</em><input type="date" value={form.subscriptionEnd || ''} onChange={e => setForm({...form, subscriptionEnd: e.target.value })} /></label>
                <label>Address<em>*</em><input required placeholder="Full address" value={form.address || ''} onChange={e => setForm({...form, address: e.target.value })} /></label>
              </div>
              <div className="form-note">âœ¦ &nbsp;You can assign a seat and payment plan after adding the student.</div>
              <div className="modal-actions"><button type="button" className="button ghost" onClick={close}>Cancel</button><button type="submit" className="button primary">{editingStudent? 'Save changes' : 'Add student'} <span>â†’</span></button></div>
            </form>
            <SeatSelectionModal
              isOpen={showSeatModal}
              onClose={() => setShowSeatModal(false)}
              preSelectedLibrary={form.library}
              studentName={form.name}
              onSeatSelected={(s) => setForm({...form, seat: s.seatnumber, seat_id: s.id, room_id: s.room_id, library_id: s.library_id})}
            />
          </>
        ) : employee? (
          <form onSubmit={saveEmployee}>
            <div className="form-grid">
              <label>Full Name <em>*</em><input required autoFocus placeholder="Employee full name" value={form.name} onChange={e => setForm({...form, name: e.target.value })} /></label>
              <label>Email <em>*</em><input required type="email" placeholder="john@example.com" value={form.email} onChange={e => setForm({...form, email: e.target.value })} /></label>
              <label>Phone <em>*</em><input required type="tel" placeholder="+91 98765 43210" value={form.phone} onChange={e => setForm({...form, phone: e.target.value })} /></label>
              <label>Role<UiSelect value={form.role} onChange={e => setForm({...form, role: e.target.value })}><option>Staff</option><option>Manager</option><option>Receptionist</option><option>Administrator</option></UiSelect></label>
              <label>Library <em>*</em><UiSelect required value={form.library} onChange={e => setForm({...form, library: e.target.value })}><option>Select Library</option><option>Pune Central</option><option>Beed</option><option>Hadapsar</option><option>Kharadi</option><option>Wagholi</option><option>Manhore Study Room</option><option>TestLab3</option></UiSelect></label>
              <label>Joining Date <em>*</em><input required type="date" value={form.joiningDate} onChange={e => setForm({...form, joiningDate: e.target.value })} /></label>
              <label>Status<UiSelect value={form.status} onChange={e => setForm({...form, status: e.target.value })}><option>Active</option><option>Inactive</option></UiSelect></label>
            </div>
            <div className="modal-actions"><button type="button" className="button ghost" onClick={close}>Cancel</button><button type="submit" className="button primary">Add Employee <span>â†’</span></button></div>
          </form>
        ) : (
          <form onSubmit={e => { e.preventDefault(); close(); toast(`${title} saved`); }}>
            <div className="form-grid"><label>Name<input required placeholder={`${title.replace('Add ', '')} name`} /></label><label>Library<UiSelect><option>Beed</option><option>Pune Central</option><option>Hadapsar</option></UiSelect></label><label>Notes<input placeholder="Optional details" /></label></div>
            <div className="modal-actions"><button type="button" className="button ghost" onClick={close}>Cancel</button><button className="button primary">Save changes <span>â†’</span></button></div>
          </form>
        )}
      </div>
    </div>
  );
}
